<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import AppointmentsHeader from './components/AppointmentsHeader.vue';
import AppointmentsSidebar from './components/AppointmentsSidebar.vue';
import DayGridView from './components/DayGridView.vue';
import ListView from './components/ListView.vue';
import AgendaView from './components/AgendaView.vue';
import AppointmentDetailPanel from './components/AppointmentDetailPanel.vue';
import AppointmentEditorModal from './components/AppointmentEditorModal.vue';
import ArchivedAppointmentsModal from './components/ArchivedAppointmentsModal.vue';

const store = useStore();

const date = ref(new Date().toLocaleDateString('en-CA'));
const businessHours = ref({ start: '08:00', end: '20:00' });
const selectedAppointmentId = ref(null);
const editorPrefill = ref(null);
const showEditor = ref(false);
const archivedModalOpen = ref(false);
const agendaViewRef = ref(null);
const agendaOverride = ref(null);

const appointments = computed(
  () => store.getters['painelAppointments/getAppointments']
);

const selectedAppointment = computed(() => {
  if (!selectedAppointmentId.value) return null;
  return (
    appointments.value.find(a => a.id === selectedAppointmentId.value) ??
    agendaOverride.value ??
    null
  );
});
const isFetching = computed(
  () => store.getters['painelAppointments/getAppointmentsUiFlags'].isFetching
);
const viewMode = computed(() => store.getters['painelView/getViewMode']);
const isMobile = useBreakpoints(breakpointsTailwind).smaller('md');
const effectiveViewMode = computed(() =>
  isMobile.value ? 'list' : viewMode.value
);

const fetchAppointments = () =>
  store.dispatch('painelAppointments/fetchByDate', {
    date: date.value,
    includeCancelled: true,
  });

const onChangeDate = d => {
  date.value = d;
  fetchAppointments();
};

const openEditorAt = ({ startMinutes }) => {
  const startsAt = new Date(`${date.value}T00:00:00`);
  startsAt.setMinutes(startMinutes);
  editorPrefill.value = {
    startsAt: startsAt.toISOString().slice(0, 16),
  };
  showEditor.value = true;
};

const openEditorEmpty = () => {
  editorPrefill.value = null;
  showEditor.value = true;
};

const onAppointmentCreated = () => fetchAppointments();

const openFromAgenda = appt => {
  agendaOverride.value = appt;
  selectedAppointmentId.value = appt.id;
};

const onDetailUpdated = updatedAppt => {
  if (agendaOverride.value?.id === updatedAppt.id) {
    agendaOverride.value = updatedAppt;
  }
  agendaViewRef.value?.patchItem(updatedAppt);
};

const onDetailClose = () => {
  selectedAppointmentId.value = null;
  agendaOverride.value = null;
};

onMounted(async () => {
  const currentUser = store.getters.getCurrentUser;
  const accountId = store.getters.getCurrentAccountId;
  store.dispatch('painelView/hydrate', { accountId, userId: currentUser?.id });

  await store.dispatch('painelCalendar/fetchState');
  await store.dispatch('painelFieldDefinitions/fetch');

  fetchAppointments();
});
</script>

<template>
  <div
    class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <AppointmentsHeader
      :date="date"
      :is-fetching="isFetching"
      @change-date="onChangeDate"
      @new-appointment="openEditorEmpty"
    />

    <div class="flex flex-1 overflow-hidden">
      <AppointmentsSidebar
        :selected-date="date"
        @select-date="onChangeDate"
        @open-archived="archivedModalOpen = true"
      />
      <div class="relative flex flex-1 flex-col overflow-hidden">
        <DayGridView
          v-if="effectiveViewMode === 'day'"
          :date="date"
          :appointments="appointments"
          :business-hours="businessHours"
          @open-appointment="a => (selectedAppointmentId = a.id)"
          @create-at="openEditorAt"
        />
        <ListView
          v-else-if="effectiveViewMode === 'list'"
          :appointments="appointments"
          @open-appointment="a => (selectedAppointmentId = a.id)"
        />
        <AgendaView
          v-else-if="effectiveViewMode === 'agenda'"
          ref="agendaViewRef"
          @open-appointment="openFromAgenda"
        />
        <AppointmentDetailPanel
          :appointment="selectedAppointment"
          @close="onDetailClose"
          @updated="onDetailUpdated"
        />
        <button
          class="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-n-brand px-5 py-3 text-sm font-semibold text-white shadow-lg md:hidden"
          @click="openEditorEmpty"
        >
          <span class="i-lucide-plus text-base" />
          {{ $t('PAINEL.APPOINTMENTS.HEADER.NEW_APPOINTMENT') }}
        </button>
      </div>
    </div>

    <AppointmentEditorModal
      v-if="showEditor"
      :prefill="editorPrefill"
      @close="showEditor = false"
      @created="onAppointmentCreated"
    />
    <ArchivedAppointmentsModal
      :open="archivedModalOpen"
      @close="archivedModalOpen = false"
    />
  </div>
</template>
